import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, Send, MessageCircle } from "lucide-react";
import { API_BASE } from "../components/AuthContext";

const INPUT_CLS = "w-full bg-white border border-[var(--border)] rounded-md px-3 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)] transition-colors";

const AU_STATES = [
  { code: "NSW", label: "New South Wales" },
  { code: "VIC", label: "Victoria" },
  { code: "QLD", label: "Queensland" },
  { code: "WA",  label: "Western Australia" },
  { code: "SA",  label: "South Australia" },
  { code: "TAS", label: "Tasmania" },
  { code: "ACT", label: "Australian Capital Territory" },
  { code: "NT",  label: "Northern Territory" },
];

export default function PublicIntake() {
  const { slug } = useParams();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", phone_is_whatsapp: false, state: "", message: "",
    scenario_tags: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/public/intake/${slug}`);
      setConfig(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Intake form unavailable");
    } finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const toggleTag = (t) => {
    setForm((p) => ({
      ...p,
      scenario_tags: p.scenario_tags.includes(t)
        ? p.scenario_tags.filter((x) => x !== t)
        : [...p.scenario_tags, t],
    }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { setError("Name is required"); return; }
    setBusy(true); setError(null);
    try {
      const body = { ...form };
      if (!body.email) delete body.email;
      if (!body.phone) delete body.phone;
      if (!body.state) delete body.state;
      if (!body.message) delete body.message;
      if (!body.scenario_tags.length) delete body.scenario_tags;
      if (!body.phone_is_whatsapp) delete body.phone_is_whatsapp;
      const r = await axios.post(`${API_BASE}/public/intake/${slug}`, body);
      setSubmitted(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed — please try again");
    } finally { setBusy(false); }
  };

  const accent = config?.accent_color || "var(--accent)";

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--paper)] flex items-center justify-center">
        <Loader2 className="animate-spin text-[var(--ink-3)]" size={24}/>
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="min-h-screen bg-[var(--paper)] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="intake-error">
          <AlertCircle className="mx-auto text-[var(--ink-3)] mb-3" size={32}/>
          <div className="font-heading text-2xl mb-2" style={{ fontWeight: 500 }}>Not available</div>
          <div className="text-[var(--ink-2)] text-sm">{error}</div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[var(--paper)] flex items-center justify-center px-6">
        <div className="max-w-lg text-center" data-testid="intake-success">
          <CheckCircle2 className="mx-auto mb-4" size={48} style={{ color: accent }}/>
          <div className="font-heading text-4xl mb-3" style={{ fontWeight: 500 }}>
            Thank you, <em className="italic" style={{ color: accent }}>{form.name.split(" ")[0]}</em>
          </div>
          <div className="text-[var(--ink-2)] text-[15px] leading-relaxed">
            {submitted.message}
          </div>
          <div className="text-[10px] text-[var(--ink-3)] mt-8 uppercase tracking-wider">
            {config.firm_name}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--paper)] py-12 px-6">
      <div className="max-w-xl mx-auto" data-testid="intake-form-page">
        <div className="text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)] mb-4">
          {config.firm_name}
        </div>
        <h1 className="font-heading text-5xl mb-3" style={{ fontWeight: 500 }}>
          {config.heading.split(/\s+/).map((word, i, arr) => i === arr.length - 1
            ? <em key={`${i}-${word}`} className="italic" style={{ color: accent }}>{word}</em>
            : <span key={`${i}-${word}`}>{word} </span>)}
        </h1>
        <p className="text-[var(--ink-2)] text-[15px] leading-relaxed mb-10">
          {config.blurb}
        </p>

        <form onSubmit={submit} className="space-y-5" data-testid="intake-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Full name *" required>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={INPUT_CLS} data-testid="intake-name"/>
            </Field>
            <Field label="Email">
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={INPUT_CLS} data-testid="intake-email"/>
            </Field>
            <Field label="Phone">
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={INPUT_CLS} data-testid="intake-phone"/>
              <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer select-none" data-testid="intake-whatsapp-wrap">
                <input
                  type="checkbox"
                  checked={form.phone_is_whatsapp}
                  onChange={(e) => setForm({ ...form, phone_is_whatsapp: e.target.checked })}
                  className="accent-[var(--accent)]"
                  data-testid="intake-whatsapp"
                />
                <MessageCircle size={11} className="text-[var(--ink-3)]" />
                <span className="text-[11px] text-[var(--ink-3)]">This number is on WhatsApp</span>
              </label>
            </Field>
            <Field label="State / Territory">
              <select
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
                className={INPUT_CLS}
                data-testid="intake-state"
              >
                <option value="">— Select —</option>
                {AU_STATES.map((s) => (
                  <option key={s.code} value={s.code}>{s.code} — {s.label}</option>
                ))}
              </select>
            </Field>
          </div>

          {config.scenario_tags?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[var(--ink-3)] mb-2">What applies to you?</div>
              <div className="flex flex-wrap gap-1.5" data-testid="intake-tags">
                {config.scenario_tags.map((t) => {
                  const on = form.scenario_tags.includes(t);
                  return (
                    <button type="button" key={t} onClick={() => toggleTag(t)}
                      className="text-[11px] px-3 py-1.5 rounded-full transition-all"
                      style={{
                        background: on ? "var(--ink)" : "transparent",
                        color: on ? "var(--paper)" : "var(--ink-3)",
                        border: `1px solid ${on ? "var(--ink)" : "var(--border)"}`,
                      }}
                      data-testid={`intake-tag-${t}`}>
                      {t.replace(/_/g, " ")}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <Field label="Tell us more (optional)">
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={4} className={`${INPUT_CLS} resize-y`} placeholder="Anything we should know up-front?" data-testid="intake-message"/>
          </Field>

          {error && (
            <div className="text-[12px] text-[#B91C1C] flex items-center gap-2" data-testid="intake-form-error">
              <AlertCircle size={13}/> {error}
            </div>
          )}

          <button type="submit" disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-[14px] font-medium text-[var(--paper)] transition-all hover:opacity-90 disabled:opacity-60"
            style={{ background: accent }}
            data-testid="intake-submit">
            {busy ? <Loader2 size={15} className="animate-spin"/> : <Send size={15}/>}
            {busy ? "Submitting…" : "Submit enquiry"}
          </button>
          <div className="text-[10px] text-[var(--ink-3)] text-center pt-2">
            We respect your privacy. Your details go straight to the practice's lead pipeline — no third-party tracking.
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wider text-[var(--ink-3)] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
